/**
 * ADMIN MODULE — Nurture Templates (reusable email FORMATS).
 * Build a branded shell (logo, header/footer colors, button color) once, then
 * apply it to any campaign step. Edits nurture_email_templates with a live
 * preview of the chrome. The send engine renders each step inside the format.
 */

import { useEffect, useState, useCallback } from "react";
import { listFormats, createFormat, saveFormat, deleteFormat } from "../utils/nurtureService.js";

const PLACEMENTS = ["left", "center", "right"];
const input = { width: "100%", boxSizing: "border-box", border: "1px solid var(--border)", borderRadius: 7, padding: "9px 11px", fontSize: 13.5, fontFamily: "inherit", marginTop: 5 };
const label = { fontSize: 12, fontWeight: 700, color: "var(--muted)", display: "block", marginTop: 14 };

function ColorField({ lbl, value, onChange }) {
  return (
    <label style={label}>{lbl}
      <span style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 5 }}>
        <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#000000"} onChange={(e) => onChange(e.target.value)} style={{ width: 40, height: 36, padding: 2, border: "1px solid var(--border)", borderRadius: 6, background: "var(--field)", cursor: "pointer", flex: "0 0 auto" }} />
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} maxLength={9} spellCheck={false} style={{ ...input, marginTop: 0 }} />
      </span>
    </label>
  );
}

function FormatPreview({ f }) {
  const headerBg = f.header_bg_color || "#1B2A4A";
  const footerBg = f.footer_bg_color || "#F6F7FA";
  const btn = f.button_color || "#E85D26";
  const place = f.logo_placement === "center" ? "center" : f.logo_placement === "right" ? "right" : "left";
  return (
    <div style={{ border: "1px solid #E4E7EE", borderRadius: 12, overflow: "hidden", background: "#EEF1F6", padding: 16 }}>
      <div style={{ maxWidth: 420, margin: "0 auto", boxShadow: "0 2px 12px rgba(0,0,0,.08)", borderRadius: 10, overflow: "hidden" }}>
        <div style={{ background: headerBg, padding: "18px 22px", textAlign: place }}>
          {f.logo_url
            ? <img src={f.logo_url} alt="" style={{ maxHeight: 30, maxWidth: 180, display: "inline-block" }} />
            : <span style={{ color: "#fff", fontWeight: 800, fontSize: 16, letterSpacing: ".02em" }}>BRAND COMMAND</span>}
          {f.header_text ? <div style={{ color: "rgba(255,255,255,.85)", fontSize: 11.5, marginTop: 5 }}>{f.header_text}</div> : null}
        </div>
        <div style={{ background: "#fff", padding: "22px 22px 8px" }}>
          <p style={{ margin: "0 0 12px", fontSize: 14, color: "#1B2A4A" }}>Hi Ryan,</p>
          <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "#374151", lineHeight: 1.6 }}>This is how your email body sits inside the template. Each campaign step provides its own copy here.</p>
          <span style={{ display: "inline-block", background: btn, color: "#fff", fontWeight: 700, fontSize: 13.5, padding: "11px 22px", borderRadius: 7 }}>Download the guide →</span>
        </div>
        <div style={{ background: footerBg, padding: "16px 22px 20px" }}>
          {f.footer_text ? <p style={{ fontSize: 11.5, color: "#5A6478", margin: "0 0 8px" }}>{f.footer_text}</p> : null}
          <p style={{ fontSize: 11.5, color: "#8A92A3", margin: "0 0 5px" }}>Johnson Street Partners, LLC · [mailing address]</p>
          <p style={{ fontSize: 11.5, color: "#8A92A3", margin: 0 }}>You're receiving this because you downloaded a guide. <span style={{ color: "#8A92A3", textDecoration: "underline" }}>Unsubscribe</span>.</p>
        </div>
      </div>
    </div>
  );
}

export default function NurtureTemplateBuilderPanel() {
  const [rows, setRows] = useState([]);
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { const r = await listFormats(); setRows(r); setSelId((cur) => cur ?? (r[0]?.id ?? null)); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const row = rows.find((r) => r.id === selId); setDraft(row ? { ...row } : null); setMsg(null); }, [selId, rows]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  async function onSave() {
    if (!draft) return;
    setBusy(true); setErr(null); setMsg(null);
    try { await saveFormat(draft.id, draft); setMsg("Template saved."); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function onNew() {
    setBusy(true); setErr(null);
    try { const id = await createFormat("New template"); await load(); setSelId(id); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  async function onDelete() {
    if (!draft || draft.is_default) return;
    if (!window.confirm(`Delete the "${draft.name}" template? Steps using it fall back to the campaign default.`)) return;
    setBusy(true); setErr(null);
    try { await deleteFormat(draft.id); setSelId(null); await load(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <>
      {err && <div className="admin-banner" role="alert" style={{ marginBottom: 12 }}>{err}</div>}
      {msg && <div style={{ background: "var(--success-soft)", color: "var(--success)", border: "1px solid rgba(31,157,107,.25)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 14 }}>{msg}</div>}
      <p style={{ fontSize: 13, color: "var(--muted)", margin: "0 0 16px" }}>
        Build a reusable email format (logo + colors), then apply it to any campaign step on the Campaigns tab.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 22, alignItems: "start" }}>
        <div>
          <div style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "var(--surface)", marginBottom: 10 }}>
            {loading && <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>Loading…</div>}
            {!loading && rows.length === 0 && <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>No templates yet.</div>}
            {rows.map((r) => (
              <button key={r.id} onClick={() => setSelId(r.id)} style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid var(--hairline)", background: r.id === selId ? "var(--sunken)" : "var(--surface)", padding: "12px 14px", cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "var(--heading)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                  {r.is_default && <span style={{ fontSize: 9.5, fontWeight: 700, color: "var(--o-text)" }}>DEFAULT</span>}
                </div>
              </button>
            ))}
          </div>
          <button className="btn" onClick={onNew} disabled={busy} style={{ width: "100%" }}>+ New template</button>
        </div>

        <div>
          {!draft ? (
            <p style={{ color: "var(--muted)", fontSize: 14 }}>{rows.length ? "Select a template to edit." : "Create a template to get started."}</p>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 22, alignItems: "start" }}>
              <div style={{ border: "1px solid var(--border)", borderRadius: 12, padding: 18, background: "var(--surface)" }}>
                <label style={label}>Template name<input style={input} value={draft.name || ""} onChange={(e) => set("name", e.target.value)} /></label>
                <label style={label}>Logo URL <span style={{ fontWeight: 400, color: "var(--muted)" }}>(PNG/JPG — SVG won't show in most email clients)</span>
                  <input style={input} value={draft.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://app.brandcommand.app/brand-command-logo-white.png" spellCheck={false} />
                </label>
                <label style={label}>Logo placement
                  <select style={input} value={draft.logo_placement || "left"} onChange={(e) => set("logo_placement", e.target.value)}>
                    {PLACEMENTS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <ColorField lbl="Header background" value={draft.header_bg_color} onChange={(v) => set("header_bg_color", v)} />
                <label style={label}>Header tagline <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
                  <input style={input} value={draft.header_text || ""} onChange={(e) => set("header_text", e.target.value)} />
                </label>
                <ColorField lbl="Button color" value={draft.button_color} onChange={(v) => set("button_color", v)} />
                <ColorField lbl="Footer background" value={draft.footer_bg_color} onChange={(v) => set("footer_bg_color", v)} />
                <label style={label}>Footer text <span style={{ fontWeight: 400, color: "var(--muted)" }}>(optional)</span>
                  <input style={input} value={draft.footer_text || ""} onChange={(e) => set("footer_text", e.target.value)} />
                </label>
                <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
                  <button className="btn" onClick={onSave} disabled={busy}>{busy ? "…" : "Save template"}</button>
                  {!draft.is_default && <button className="secondary" onClick={onDelete} disabled={busy} style={{ color: "var(--error)" }}>Delete</button>}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--muted)", marginBottom: 8 }}>Live preview</div>
                <FormatPreview f={draft} />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
